import React, { useState, useEffect } from 'react';
import axios from '../api';
import { resolveImageUrl } from '../utils/imageUrl';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useToast } from './ToastProvider';

const modalPaperSx = {
  borderRadius: 2,
  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
  bgcolor: 'background.paper',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
};

const dialogTitleSx = {
  fontSize: { xs: '1rem', sm: '1.125rem' },
  fontWeight: 600,
  color: 'text.primary',
  px: { xs: 2, sm: 3 },
  py: { xs: 1.5, sm: 2 },
  borderBottom: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const labelSx = {
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'text.secondary',
  mb: 0.5,
  display: 'block',
};

const sectionTitleSx = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'text.primary',
  mb: 1.5,
  display: 'block',
};

const inputFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.25,
    fontSize: '0.875rem',
    '& fieldset': { borderColor: 'divider' },
  },
};

const ProductForm = ({ onAdd, closeModal, editingProduct }) => {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    actual_quantity: '',
    current_quantity: '',
    product_code: '',
    category: '',
    description: '',
    image: null,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const showToast = useToast();

  useEffect(() => {
    if (editingProduct) {
      setFormData({
        name: editingProduct.name || '',
        price: editingProduct.price || '',
        actual_quantity: editingProduct.actual_quantity || '',
        current_quantity: editingProduct.current_quantity || '',
        product_code: editingProduct.product_code || '',
        category: editingProduct.category || '',
        description: editingProduct.description || '',
        image: null,
        is_active: editingProduct.is_active !== false,
      });
    }
  }, [editingProduct]);

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (files ? files[0] : value),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    const data = new FormData();
    Object.entries(formData).forEach(([key, val]) => {
      if (val === null || (key !== 'is_active' && val === '')) return;
      if (key === 'is_active') {
        data.append(key, val ? 'true' : 'false');
      } else {
        data.append(key, val);
      }
    });
    try {
      if (editingProduct) {
        await axios.put(`/api/products/${editingProduct.id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showToast('Product updated successfully', 'success');
      } else {
        await axios.post('/api/products', data);
        showToast('Product added successfully', 'success');
      }
      onAdd();
      closeModal();
      setFormData({ name: '', price: '', actual_quantity: '', product_code: '', description: '', image: null, is_active: true });
    } catch (error) {
      setErrorMsg('Error saving product.');
      showToast('Error saving product.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
      sx={{ '& .MuiDialog-paper': { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <span>{editingProduct ? 'Edit Product' : 'Add Product'}</span>
        <IconButton size="small" onClick={closeModal} sx={{ color: 'text.secondary' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto', flex: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
        <Box component="form" onSubmit={handleSubmit} id="product-form">
          <Typography sx={sectionTitleSx}>Details</Typography>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12}>
              <Typography sx={labelSx}>Product Name *</Typography>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="e.g. Ganapathy Idol"
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Product Price *</Typography>
              <TextField
                name="price"
                type="number"
                value={formData.price}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="e.g. 100"
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Product Code *</Typography>
              <TextField
                name="product_code"
                value={formData.product_code}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="e.g. GN001"
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Category</Typography>
              <TextField
                name="category"
                value={formData.category}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="e.g. GN"
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 0, sm: 2.5 } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
          <Typography sx={sectionTitleSx}>Quantity</Typography>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Actual Quantity *</Typography>
              <TextField
                name="actual_quantity"
                type="number"
                value={formData.actual_quantity}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="e.g. 100"
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Current Quantity *</Typography>
              <TextField
                name="current_quantity"
                type="number"
                value={formData.current_quantity}
                onChange={handleChange}
                required
                fullWidth
                size="small"
                placeholder="e.g. 95"
                sx={inputFieldSx}
              />
            </Grid>
          </Grid>
          <Typography sx={sectionTitleSx}>Description</Typography>
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={labelSx}>Description</Typography>
            <TextField
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              size="small"
              multiline
              minRows={3}
              placeholder="Optional description..."
              sx={inputFieldSx}
            />
          </Box>
          <Typography sx={sectionTitleSx}>Image</Typography>
          <Box>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<CloudUploadIcon />}
              sx={{
                minHeight: 100,
                borderRadius: 1.25,
                borderStyle: 'dashed',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                color: 'text.secondary',
                textTransform: 'none',
                fontSize: '0.875rem',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              Upload Product Image
              <input type="file" name="image" hidden accept="image/*" onChange={handleChange} />
            </Button>
            {editingProduct?.image_url && (
              <Box sx={{ mt: 1 }}>
                <Box
                  component="img"
                  src={resolveImageUrl(editingProduct.image_url)}
                  alt="Current"
                  sx={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain', borderRadius: 1 }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Button variant="outlined" onClick={closeModal} disabled={loading} sx={{ textTransform: 'none', fontWeight: 500 }}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="product-form"
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          {loading ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} sx={{ color: 'white' }} />{editingProduct ? 'Updating...' : 'Adding...'}</Box> : (editingProduct ? 'Update Product' : 'Add Product')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductForm;
